import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { OpenSearchQueryBuilder, classifySearchToken } from 'tio-common/utils/open-search-builder';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import { executeSearchRequest } from 'tio-data/requests/search';
import type { FilterClause, NestedClause } from 'tio-common/utils/open-search-builder';
import type { OpenSearchResponse } from 'tio-data/data/resources/search.ext';
import type Transition from '@ember/routing/transition';
import type { Store } from '@warp-drive/core';
import type PartnerService from 'tio-employee/services/partner';

type Params = {
  sort: string;
  search: string;
  participationStatus: string;
  page: number;
  limit: number;
};

/** One row of the employee table, projected from a Person hit's company employee. */
export type EmployeeRow = {
  id: string;
  payrollId: string;
  firstName: string;
  lastName: string;
  email: string;
  participationStatus: string;
  createdAt?: string;
};

export type EmployeeIndexModel = {
  items: EmployeeRow[];
  total: number;
  isLoading: boolean;
};

/**
 * One nested employee entry on a Person `_source`. The search query is sent to
 * OpenSearch raw in snake_case, but the API serializes the response `_source` to
 * camelCase (Accept: application/vnd.api+json), so we read camelCase here and keep
 * snake_case as a defensive fallback.
 */
type EmployeesNestedEntry = {
  employeeIdKeyword?: string | number;
  firstNameText?: string;
  lastNameText?: string;
  payrollIdKeyword?: string;
  emailText?: string;
  companyIdKeyword?: string | number;
  participationStatusKeyword?: string;
  createdAtDate?: string;
  // snake_case fallbacks (in case a response is not camelized)
  employee_id_keyword?: string | number;
  first_name_text?: string;
  last_name_text?: string;
  payroll_id_keyword?: string;
  email_text?: string;
  company_id_keyword?: string | number;
  participation_status_keyword?: string;
  created_at_date?: string;
};

type PersonHitSource = {
  employeesNested?: EmployeesNestedEntry[];
  employees_nested?: EmployeesNestedEntry[];
};

/**
 * Sort token (the template's `@sortField`) → nested `employees_nested` field.
 * Sorts run as a nested sort filtered to the partner company (see `nestedSort`).
 * Every visible column maps to a keyword/date field that is sortable as-is. The
 * name/email columns sort on the dedicated `employee_*_keyword` fields rather
 * than the analyzed `*_text` fields (which aren't sortable without a subfield).
 */
const SORTABLE_NESTED_FIELDS: Record<string, string> = {
  'created-at': 'employees_nested.created_at_date',
  'payroll-id': 'employees_nested.payroll_id_keyword',
  'participation-status': 'employees_nested.participation_status_keyword',
  'first-name': 'employees_nested.employee_first_name_keyword',
  'last-name': 'employees_nested.employee_last_name_keyword',
  email: 'employees_nested.employee_email_keyword',
};

export default class AuthenticatedAdminEmployeesRoute extends Route {
  @service declare store: Store;
  @service declare partner: PartnerService;

  queryParams = {
    search: { refreshModel: true, replace: true },
    sort: { refreshModel: true, replace: true },
    participationStatus: { refreshModel: true, replace: true },
    page: { refreshModel: true, replace: true },
    limit: { refreshModel: true, replace: true },
  };

  async model({
    sort,
    search,
    participationStatus,
    page,
    limit,
  }: Params): Promise<EmployeeIndexModel> {
    const companyId = this.partner.company?.id;

    // SearchPolicy requires the company scope for company-admin/employee roles.
    // Without a company we have nothing to scope to — return an empty result.
    if (!companyId) {
      return { items: [], total: 0, isLoading: false };
    }

    // Coerce + clamp page/limit so a malformed query param (missing, non-numeric,
    // or < 1) can't flow into from/size as NaN.
    const safePage = Math.max(1, Number(page) || 1);
    const safeLimit = Math.max(1, Number(limit) || 50);

    const builder = new OpenSearchQueryBuilder().setPagination(safePage, safeLimit);

    // The top-level term satisfies SearchPolicy (it requires a company filter), and a
    // nested filter on employees_nested scopes to THIS company's employee entry. The
    // nested scope is applied ALWAYS (not only when a status is chosen) so the hit
    // count matches the rows we can project — a person can appear in the top-level
    // company_ids via a non-employee relationship and would otherwise inflate `total`
    // without a projectable row. The status term, when set, folds into the same nested
    // filter so it only matches this company's employee, never another company's entry
    // on the same person.
    builder.mustTerm('company_ids', companyId);
    builder.addFilter(this.companyEmployeeFilter(companyId, participationStatus));

    this.applyRoutedSearch(builder, search);

    const body = builder.build() as Record<string, unknown>;

    // Replace the builder's flat sort with a company-scoped nested sort.
    body.sort = this.nestedSort(companyId, sort);

    const query = {
      'model-type': 'Person',
      'search-body': body,
    };

    const { content: response } = (await this.store.request(executeSearchRequest(query))) as {
      content: OpenSearchResponse;
    };

    const rb = OpenSearchResponseBuilder.from(response);
    const total = rb.getTotalCount();

    // Each hit is a Person; project the company's employee entry to one row.
    const items = (response.hits?.hits ?? [])
      .map((hit) => this.rowFromPersonHit(hit._source as PersonHitSource, companyId))
      .filter((row): row is EmployeeRow => row !== undefined);

    return { items, total, isLoading: false };
  }

  /**
   * Build the search box query. Tokens are classified (email / identifier /
   * name) and routed to the right top-level fields: names fuzzily, payroll ids
   * and emails exactly. Clauses are OR-combined under bool.must.
   */
  private applyRoutedSearch(builder: OpenSearchQueryBuilder, search?: string) {
    const trimmed = search?.trim();
    if (!trimmed) return;

    const tokens = trimmed.split(/\s+/).filter(Boolean);
    const nameTokens: string[] = [];

    for (const token of tokens) {
      const kind = classifySearchToken(token);
      if (kind === 'email') {
        builder.addSearchShould({
          match: { all_emails: { query: token, operator: 'and', _name: 'email' } },
        });
      } else if (kind === 'identifier') {
        // Non-fuzzy match on analyzed text keeps the token as one term (exact).
        builder.addSearchShould({ match: { all_payroll_ids: { query: token, _name: 'payroll' } } });
      } else {
        nameTokens.push(token);
      }
    }

    if (nameTokens.length) {
      builder.addSearchShould({
        multi_match: {
          query: nameTokens.join(' '),
          fields: ['all_employee_names'],
          type: 'best_fields',
          operator: 'or',
          fuzziness: 'AUTO',
          lenient: true,
          _name: 'name',
        },
      });
    }
  }

  /**
   * A `nested` filter scoped to the partner company's employee entry, optionally
   * narrowed to a participation status. Always company-scoped so `total` reflects
   * only persons with a projectable employee row at this company.
   */
  private companyEmployeeFilter(companyId: string, participationStatus?: string): NestedClause {
    const filter: FilterClause[] = [{ term: { 'employees_nested.company_id_keyword': companyId } }];

    if (participationStatus) {
      // The index stores the status lowercased ('participating' / 'pending' /
      // 'deactivated' — see tio-api Employee#participation_status); the Select
      // surfaces capitalized labels, so normalize before the case-sensitive term.
      filter.push({
        term: {
          'employees_nested.participation_status_keyword': participationStatus.toLowerCase(),
        },
      });
    }

    return {
      nested: {
        path: 'employees_nested',
        query: { bool: { filter } },
      },
    };
  }

  /**
   * Default `-created-at` (newest first). Mapped column sorts use a nested sort
   * on `employees_nested.<field>` filtered to the company so the sort key comes
   * from THIS company's employee entry. Unmapped tokens (text columns) fall back
   * to created_at_date desc.
   */
  private nestedSort(companyId: string, sort?: string) {
    const token = (sort || '-created-at').trim();
    const desc = token.startsWith('-');
    const key = desc ? token.slice(1) : token;
    const field = SORTABLE_NESTED_FIELDS[key] ?? 'employees_nested.created_at_date';
    const order = SORTABLE_NESTED_FIELDS[key] ? (desc ? 'desc' : 'asc') : 'desc';

    const companyNested = {
      path: 'employees_nested',
      filter: { term: { 'employees_nested.company_id_keyword': companyId } },
    };

    return [
      { [field]: { order, nested: companyNested } },
      // Stable tiebreaker so `from` pagination can't duplicate/skip rows when the
      // primary key ties: employee_id_keyword is unique per company employee.
      { 'employees_nested.employee_id_keyword': { order: 'asc', nested: companyNested } },
    ];
  }

  /**
   * Project a Person hit to a single table row using the employee entry that
   * belongs to the partner company. Returns undefined if the person has no
   * employee at this company (shouldn't happen given the company filter).
   */
  private rowFromPersonHit(source: PersonHitSource, companyId: string): EmployeeRow | undefined {
    const entries = source.employeesNested ?? source.employees_nested ?? [];
    const employee = entries.find(
      (e) => String(e.companyIdKeyword ?? e.company_id_keyword) === String(companyId)
    );
    if (!employee) return undefined;

    return {
      id: String(employee.employeeIdKeyword ?? employee.employee_id_keyword ?? ''),
      payrollId: employee.payrollIdKeyword ?? employee.payroll_id_keyword ?? '',
      firstName: employee.firstNameText ?? employee.first_name_text ?? '',
      lastName: employee.lastNameText ?? employee.last_name_text ?? '',
      email: employee.emailText ?? employee.email_text ?? '',
      participationStatus:
        employee.participationStatusKeyword ?? employee.participation_status_keyword ?? '',
      createdAt: employee.createdAtDate ?? employee.created_at_date,
    };
  }

  /**
   * Reset to the first page whenever a result-narrowing filter changes, so a
   * stale `page` offset can't land past the end of the new (smaller) result set.
   * Mirrors the pslf forms route. `sort` is excluded — it reorders but doesn't
   * change the result count.
   */
  willTransition(transition: Transition) {
    const watched = ['search', 'participationStatus'];
    const from =
      (transition.from as { queryParams?: Record<string, unknown> } | undefined)?.queryParams || {};
    const to =
      (transition.to as { queryParams?: Record<string, unknown> } | undefined)?.queryParams || {};

    const filterChanged = watched.some((p) => (from[p] || '') !== (to[p] || ''));
    if (filterChanged) {
      // eslint-disable-next-line ember/no-controller-access-in-routes
      this.controllerFor('authenticated.admin.employees.index').set('page', 1);
    }
    return true;
  }
}
